import { useEffect, useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

const UNASSIGNED = '__none__'

function teamLabel(team) {
  if (!team) return 'TBD'
  const names = (team.players || []).map(p => p.name).filter(Boolean)
  const who = names.length ? names.join(' / ') : 'Unnamed team'
  return team.seed ? `${team.seed}. ${who}` : who
}

// The builder creates a bracket_teams row per (round, match, slot), so a team
// that appears in several rounds (a play-in winner re-entered in round 1, say)
// exists as several rows. They are the same real team; collapse them on
// seed + players so the dropdown lists each team once.
function identityKey(team) {
  const players = (team.player_ids || []).slice().sort().join(',')
  if (team.seed == null && !players) return team.id
  return `${team.seed ?? ''}|${players}`
}

/**
 * One dropdown entry per real team, plus a map from every duplicate row id to
 * the id that entry uses. Keeps the latest round's row: that's the id the
 * round's published seeds — and so the official picks — reference.
 */
export function collapseTeams(seeds) {
  const byIdentity = new Map()
  seeds.forEach(s => {
    ;[s.team_one, s.team_two].forEach(t => {
      if (!t) return
      const key = identityKey(t)
      const cur = byIdentity.get(key)
      if (!cur || (t.round ?? -1) > (cur.round ?? -1)) byIdentity.set(key, t)
    })
  })
  const canonical = new Map()
  seeds.forEach(s => {
    ;[s.team_one, s.team_two].forEach(t => {
      if (t) canonical.set(t.id, byIdentity.get(identityKey(t)).id)
    })
  })
  return {
    teams: Array.from(byIdentity.values()).sort((a, b) => (a.seed ?? 999) - (b.seed ?? 999)),
    canonical,
  }
}

/**
 * Admin: author one round's matchups.
 *
 * Normally a single-elim round's participants follow from bracket position —
 * the winner of match 1 plays the winner of match 2. Some formats break that:
 * in the MLP playoffs the bye teams CHOOSE their quarterfinal opponent from the
 * first-round winners, and the quarterfinal winners choose again for the semis,
 * so the pairings are announced by the league rather than implied by the
 * bracket. Publishing them here writes the real teams onto the round's seed
 * rows and flags the round so every user picks that field, instead of each user
 * seeing their own predicted advancers.
 *
 * @param {Array} seeds - full seed list from get_bracket_with_full_details
 * @param {(matchups: Array) => Promise<void>} onPublish
 */
export default function RoundMatchupEditor({ round, matchCount, seeds, published, disabled, onPublish }) {
  const [expanded, setExpanded] = useState(false)

  // Every team in the bracket, one entry per real team, sorted by seed. The
  // admin picks from the whole field: who is still alive depends on official
  // results this component deliberately doesn't try to second-guess.
  const { teams, canonicalId } = useMemo(() => {
    const { teams, canonical } = collapseTeams(seeds)
    return { teams, canonicalId: id => (id ? canonical.get(id) || id : null) }
  }, [seeds])

  const roundSeeds = useMemo(
    () => seeds.filter(s => s.round === round).sort((a, b) => a.match_number - b.match_number),
    [seeds, round]
  )

  const [rows, setRows] = useState([])

  useEffect(() => {
    const next = []
    for (let m = 1; m <= matchCount; m++) {
      const seed = roundSeeds.find(s => s.match_number === m)
      // Map through canonicalId so a seed saved with a duplicate team row still
      // preselects the one entry the dropdown lists for that team.
      next.push({
        match_number: m,
        team_one_id: canonicalId(seed?.team_one?.id),
        team_two_id: canonicalId(seed?.team_two?.id),
      })
    }
    setRows(next)
  }, [roundSeeds, matchCount, canonicalId])

  const setSlot = (matchNumber, slot, value) => {
    const id = value === UNASSIGNED ? null : value
    setRows(rs => rs.map(r => (r.match_number === matchNumber ? { ...r, [slot]: id } : r)))
  }

  // A team appearing twice in one round is always a mistake — the same team
  // cannot play two matches in the same round.
  const duplicateIds = useMemo(() => {
    const seen = new Map()
    rows.forEach(r => {
      ;[r.team_one_id, r.team_two_id].forEach(id => {
        if (id) seen.set(id, (seen.get(id) || 0) + 1)
      })
    })
    return new Set(Array.from(seen.entries()).filter(([, n]) => n > 1).map(([id]) => id))
  }, [rows])

  if (!expanded) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setExpanded(true)} className="text-xs">
        {published ? 'Edit matchups' : 'Set matchups'}
      </Button>
    )
  }

  return (
    <div className="rounded-md bg-[var(--mauve-2)] p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          Matchups for this round
          {published && <span className="ml-2 text-[var(--teal-11)]">published</span>}
        </span>
        <Button size="sm" variant="ghost" onClick={() => setExpanded(false)} className="text-xs">Hide</Button>
      </div>

      {rows.map(row => (
        <div key={row.match_number} className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0">Match {row.match_number}</span>
          {['team_one_id', 'team_two_id'].map(slot => (
            <Select
              key={slot}
              value={row[slot] || UNASSIGNED}
              onValueChange={v => setSlot(row.match_number, slot, v)}
              disabled={disabled}
            >
              <SelectTrigger
                className={`w-[13rem] ${row[slot] && duplicateIds.has(row[slot]) ? 'border-destructive' : ''}`}
              >
                <SelectValue placeholder="TBD" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>TBD</SelectItem>
                {teams.map(t => (
                  <SelectItem key={t.id} value={t.id}>{teamLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
        </div>
      ))}

      {duplicateIds.size > 0 && (
        <p className="text-xs text-destructive">
          A team is entered in more than one match this round. Fix that before publishing.
        </p>
      )}

      <Button
        size="sm"
        disabled={disabled || duplicateIds.size > 0}
        onClick={() => onPublish(rows)}
      >
        <Check className="h-4 w-4 mr-1" /> Publish matchups
      </Button>
      <p className="text-xs text-muted-foreground">
        Publishing replaces this round&apos;s matchups for everyone. Users pick the real field, so a
        wrong pick in an earlier round costs those points but doesn&apos;t change who they see here.
      </p>
    </div>
  )
}
